import { srConfig } from '@config';
import { usePrefersReducedMotion } from '@hooks';
import sr from '@utils/sr';

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const StyledSkillsSection = styled.div`
  margin-top: 4rem;
  position: relative;
  width: 100%;
`;

const StyledTabList = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 40px;
  width: 100%;

  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 30px;
  }
`;

const StyledTabButton = styled.button`
  font-family: var(--font-sans);
  font-weight: 600;
  font-size: 14px;
  padding: 10px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(8px);
  color: var(--light-slate);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 30px;
  cursor: pointer;
  letter-spacing: 0.5px;
  transition: all 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: var(--white);
    border-color: rgba(255, 42, 42, 0.4);
    transform: translateY(-2px);
  }

  &.active {
    background: rgba(255, 42, 42, 0.08);
    color: #ff2a2a;
    border-color: rgba(255, 42, 42, 0.6);
    box-shadow: 0 0 15px rgba(255, 42, 42, 0.15);

    &::before {
      content: '[';
      margin-right: 6px;
      color: #ff2a2a;
      font-family: var(--font-mono);
      font-weight: 700;
    }
    &::after {
      content: ']';
      margin-left: 6px;
      color: #ff2a2a;
      font-family: var(--font-mono);
      font-weight: 700;
    }
  }

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 8px 16px;
  }
`;

const StyledGridContainer = styled.div`
  display: ${props => (props.$active ? 'flex' : 'none')};
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  width: 100%;

  &.active {
    animation: tabFadeIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  @keyframes tabFadeIn {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const StyledSkillCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 24px 16px;
  text-align: center;
  transition: all 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);
  width: 140px;

  .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    margin-bottom: 12px;
    transition: transform 0.25s ease;

    .img {
      max-height: 100%;
      object-fit: contain;
      filter: grayscale(15%) opacity(0.85);
      transition: all 0.25s ease;
    }
  }

  .skill-name {
    font-size: var(--fz-xs);
    font-family: var(--font-mono);
    color: var(--light-slate);
    font-weight: 500;
    transition: color 0.25s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 42, 42, 0.85);
    box-shadow: 0 10px 30px -10px rgba(255, 42, 42, 0.35), 0 0 15px rgba(255, 42, 42, 0.15);
    transform: translateY(-5px);

    .icon-wrapper {
      transform: scale(1.08);

      .img {
        filter: grayscale(0%) opacity(1) drop-shadow(0 4px 12px rgba(255, 255, 255, 0.15));
      }
    }

    .skill-name {
      color: #ff2a2a;
      text-shadow: 0 0 8px rgba(255, 42, 42, 0.2);
    }
  }

  @media (max-width: 480px) {
    padding: 16px 8px;
    border-radius: 12px;
    width: 105px;

    .icon-wrapper {
      width: 48px;
      height: 48px;
      margin-bottom: 8px;
    }

    .skill-name {
      font-size: var(--fz-xxs);
    }
  }
`;

const imageSelector = imageName => {
  const fileMapping = {
    'C++': 'C++.png',
    'C#': 'csharp.png',
    Java: 'Java.png',
    Python: 'Python.png',
    SQL: 'SQL.png',
    AL: 'AL.png',
    HTML: 'Html.png',
    CSS: 'css.png',
    'Tailwind CSS': 'Tailwind-css.png',
    Bootstrap: 'bootstrap.png',
    JavaScript: 'Javascript.png',
    'Node.js': 'Node-js.png',
    'React.js': 'React-js.png',
    'Express.js': 'express-js.png',
    'Spring Boot': 'SpringBoot.png',
    Flutter: 'flutter.png',
    Git: 'git.png',
    GitHub: 'github.png',
    'VS Code': 'VS-code.png',
    Antigravity: 'antigravity.png',
    Azure: 'Azure.png',
    Trello: 'trello.png',
    'Business Central': 'BC.png',
    Figma: 'Figma.png',
    Photoshop: 'photoshop.png',
    'Material-UI': 'material-ui.png',
    MongoDB: 'mongodb.png',
    MySQL: 'mysql.png',
    'Microsoft SQL Server': 'ms_sql_server.png',
    OpenCV: 'opencv.png',
    PyTorch: 'pytorch.png',
    MLflow: 'mlflow.png',
    NumPy: 'numpy.png',
    Pandas: 'pandas.png',
    Docker: 'docker.png',
    Kubernetes: 'kubernets.png',
    'CI/CD': 'ci-cd.png',
    AWS: 'aws.png',
    'GitHub Actions': 'githubactions.png',
    Jenkins: 'jenkins.png',
    'Apache Airflow': 'airflow.png',
    Grafana: 'grafana.png',
    Postman: 'postman.png',
    Flask: 'flask.png',
  };

  const fileName = fileMapping[imageName];
  if (!fileName) {
    return null;
  }

  return (
    <img
      className="img"
      src={`/skills/${fileName}`}
      alt={imageName}
      style={{ height: '64px', objectFit: 'contain' }}
      loading="eager"
    />
  );
};

const Skills = () => {
  const [activeTab, setActiveTab] = useState('languages');
  const revealContainer = useRef(null);
  const revealTitle = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
    sr.reveal(revealTitle.current, srConfig());
  }, []);

  const categories = {
    languages: {
      name: 'Languages',
      skills: ['Python', 'JavaScript', 'Java', 'C++', 'C#', 'AL', 'SQL'],
    },
    webMobile: {
      name: 'Web & Mobile Dev',
      skills: [
        'HTML',
        'CSS',
        'Tailwind CSS',
        'Bootstrap',
        'React.js',
        'Node.js',
        'Express.js',
        'Spring Boot',
        'Flask',
        'Flutter',
      ],
    },
    aiMl: {
      name: 'AI/ML',
      skills: ['PyTorch', 'MLflow', 'OpenCV', 'NumPy', 'Pandas'],
    },
    devopsCloud: {
      name: 'DevOps & Cloud',
      skills: [
        'Docker',
        'Kubernetes',
        'Apache Airflow',
        'Grafana',
        'CI/CD',
        'GitHub Actions',
        'Jenkins',
        'AWS',
        'Azure',
      ],
    },
    databases: {
      name: 'Databases',
      skills: ['MongoDB', 'MySQL', 'Microsoft SQL Server'],
    },
    toolsIdes: {
      name: 'Tools & IDEs',
      skills: ['Antigravity', 'VS Code', 'Postman', 'Git', 'GitHub', 'Trello', 'Business Central'],
    },
    uiuxDesign: {
      name: 'UI/UX & Design',
      skills: ['Figma', 'Material-UI', 'Photoshop'],
    },
  };

  return (
    <StyledSkillsSection ref={revealContainer}>
      <StyledTabList ref={revealTitle}>
        {Object.keys(categories).map(key => (
          <StyledTabButton
            key={key}
            className={activeTab === key ? 'active' : ''}
            onClick={() => setActiveTab(key)}
          >
            {categories[key].name}
          </StyledTabButton>
        ))}
      </StyledTabList>

      {Object.keys(categories).map(key => (
        <StyledGridContainer
          key={key}
          $active={activeTab === key}
          className={activeTab === key ? 'active' : ''}
        >
          {categories[key].skills.map((skill, index) => (
            <StyledSkillCard key={index}>
              <div className="icon-wrapper">{imageSelector(skill)}</div>
              <span className="skill-name">{skill}</span>
            </StyledSkillCard>
          ))}
        </StyledGridContainer>
      ))}
    </StyledSkillsSection>
  );
};

export default Skills;
